import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BookingGenerationJob } from '../../../types';
import { retryBookingGeneration } from '../apis/bookingApi';

/**
 * Hook to retry a failed booking job. Looks up the receiptId from the
 * booking generation job cache, then calls the retry endpoint.
 * On success, invalidates messages and receipt caches so polling resumes.
 */
export const useRetryBookingJob = (chatId: string | undefined) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (bookingJobId: string) => {
      // Look up receiptId from the cached booking generation job
      const cachedJob = queryClient.getQueryData<BookingGenerationJob>([
        'booking-generation',
        bookingJobId,
      ]);
      const receiptId = cachedJob?.receiptId;

      if (!receiptId) {
        throw new Error(
          'Could not find receipt ID for this booking job. Please refresh and try again.'
        );
      }

      const token = await getAccessTokenSilently();
      await retryBookingGeneration(receiptId, token);
      return { bookingJobId, receiptId };
    },
    onSuccess: ({ bookingJobId, receiptId }) => {
      // Invalidate messages cache so polling picks up the new state
      if (chatId) {
        queryClient.invalidateQueries({ queryKey: ['messages', chatId] });
      }

      // Invalidate receipt cache
      queryClient.invalidateQueries({
        queryKey: ['uploaded-receipt', receiptId],
      });

      // Invalidate stale booking job query so polling restarts fresh
      queryClient.invalidateQueries({
        queryKey: ['booking-generation', bookingJobId],
      });
    },
  });

  const retry = useCallback(
    async (bookingJobId: string): Promise<boolean> => {
      try {
        await mutation.mutateAsync(bookingJobId);
        return true;
      } catch {
        return false;
      }
    },
    [mutation]
  );

  return {
    retryBookingJob: retry,
    isRetrying: mutation.isPending,
  };
};
